/**
 * Dashboard Query Hooks
 *
 * TanStack Query hooks for dashboard data, all routed through the API server.
 */

import { useQuery } from '@tanstack/react-query';

import { useFetchWithAuth } from './use-api';

/** Dashboard stats response shape */
interface DashboardStatsResponse {
  byStatus: Record<string, number>;
  totalActive: number;
  completedThisMonth: number;
}

/** Needs-attention item shape */
interface AttentionItem {
  id: string;
  title: string;
  status: 'create' | 'interview' | 'develop' | 'review' | 'finalize' | 'closed';
  priority: 'low' | 'medium' | 'high' | 'critical' | null;
  request_number: number;
}

/** Raw activity item from the API */
interface RawActivityItem {
  id: string;
  type: string;
  data: Record<string, unknown> | null;
  created_at: string;
  requests: { title: string; request_number: number } | null;
}

/** Project with summary response */
interface DashboardProjectsResponse {
  projects: Array<{
    id: string;
    name: string;
    slug: string;
    description: string | null;
    updated_at: string;
    [key: string]: unknown;
  }>;
  summaries: Record<
    string,
    {
      draft: number;
      pending: number;
      inProgress: number;
      review: number;
      completed: number;
    }
  >;
}

export function useDashboardStats() {
  const fetchWithAuth = useFetchWithAuth();

  return useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: async () => {
      const res = await fetchWithAuth('/dashboard/stats');
      if (!res.ok) throw new Error('Failed to fetch dashboard stats');
      const json = await res.json();
      return json.data as DashboardStatsResponse;
    },
  });
}

export function useDashboardNeedsAttention() {
  const fetchWithAuth = useFetchWithAuth();

  return useQuery({
    queryKey: ['dashboard-needs-attention'],
    queryFn: async () => {
      const res = await fetchWithAuth('/dashboard/needs-attention');
      if (!res.ok) throw new Error('Failed to fetch attention items');
      const json = await res.json();
      return json.data as AttentionItem[];
    },
  });
}

export function useDashboardActivity(limit = 8) {
  const fetchWithAuth = useFetchWithAuth();

  return useQuery({
    queryKey: ['dashboard-activity', { limit }],
    queryFn: async () => {
      const res = await fetchWithAuth(`/dashboard/activity?limit=${limit}`);
      if (!res.ok) throw new Error('Failed to fetch activity feed');
      const json = await res.json();
      return json.data as RawActivityItem[];
    },
  });
}

export function useDashboardRecentRequests(limit = 4) {
  const fetchWithAuth = useFetchWithAuth();

  return useQuery({
    queryKey: ['requests', 'recent'],
    queryFn: async () => {
      const res = await fetchWithAuth(
        `/dashboard/recent-requests?limit=${limit}`
      );
      if (!res.ok) throw new Error('Failed to fetch recent requests');
      const json = await res.json();
      return json.data as Array<{
        id: string;
        title: string;
        description: string | null;
        status: string;
        priority: string | null;
        project_id: string;
        request_number: number;
        updated_at: string;
        [key: string]: unknown;
      }>;
    },
  });
}

export function useDashboardProjects() {
  const fetchWithAuth = useFetchWithAuth();

  return useQuery({
    queryKey: ['dashboard-projects-with-summaries'],
    queryFn: async () => {
      const res = await fetchWithAuth('/dashboard/projects');
      if (!res.ok) throw new Error('Failed to fetch projects');
      const json = await res.json();
      return json.data as DashboardProjectsResponse;
    },
  });
}
